import { Target, Eye, Handshake, TrendUp, Users, Medal } from '@phosphor-icons/react';

const values = [
  { icon: Target, title: 'Mission-Driven', desc: 'Every decision we make is centered on helping students succeed in their educational journey.' },
  { icon: Eye, title: 'Transparent', desc: 'We maintain full transparency in our processes, fees, and partnerships at every step.' },
  { icon: Handshake, title: 'Trusted Partners', desc: 'We work with accredited institutions and verified recruitment partners worldwide.' },
  { icon: TrendUp, title: 'Results-Focused', desc: 'Our track record speaks for itself with thousands of successful student placements.' },
];

const timeline = [
  { year: '2018', title: 'Founded', desc: 'Study Alliance was established with a vision to simplify college admissions.' },
  { year: '2019', title: 'First 500 Students', desc: 'Reached our first milestone of placing 500 students across partner institutions.' },
  { year: '2021', title: 'Recruiter Network', desc: 'Launched our recruiter partnership program, expanding our global reach.' },
  { year: '2023', title: 'Exam Prep Launch', desc: 'Added comprehensive exam preparation support for iTEP, Accuplacer, and more.' },
  { year: '2025', title: '5,000+ Students', desc: 'Surpassed 5,000 successful student placements with a 98% success rate.' },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-blue-600 font-medium text-sm uppercase tracking-widest mb-3">About Us</p>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight mb-6">Building Bridges to Education</h1>
            <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
              Study Alliance is a leading student recruitment and admissions consultancy. We connect students with the right colleges, guide them through applications and exam preparation, and partner with recruiters worldwide to expand access to education.
            </p>
          </div>
        </div>
      </section>

      {/* Image + Mission */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <img src="https://images.unsplash.com/photo-1560426961-a3f38f5594fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHx1bml2ZXJzaXR5JTIwY2FtcHVzJTIwY2xhc3NpY3xlbnwwfHx8fDE3NzYzNjU1NjR8MA&ixlib=rb-4.1.0&q=85" alt="University campus" className="rounded-lg shadow-lg w-full object-cover max-h-[480px]" />
            </div>
            <div className="lg:col-span-6">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight mb-6">Our Mission</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                We believe every student deserves access to quality education. Our mission is to break down barriers to college admission by providing expert guidance, comprehensive exam preparation, and seamless application support.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Through our network of trusted recruitment partners, we extend our reach globally, ensuring that students from every background can find their path to academic success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-blue-600 font-medium text-sm uppercase tracking-widest mb-3">Our Values</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">What Drives Us</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map(v => (
              <div key={v.title} className="p-6 bg-white border border-gray-200 rounded-lg hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                  <v.icon size={24} className="text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{ fontFamily: "'Libre Baskerville', serif" }}>{v.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-blue-600 font-medium text-sm uppercase tracking-widest mb-3">Our Journey</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">Key Milestones</h2>
          </div>
          <div className="max-w-3xl mx-auto">
            {timeline.map((t, i) => (
              <div key={t.year} className="flex gap-6 mb-8 last:mb-0">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-blue-800 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">{t.year}</div>
                  {i < timeline.length - 1 && <div className="w-px h-full bg-gray-200 mt-2" />}
                </div>
                <div className="pt-2 pb-8">
                  <h3 className="text-lg font-semibold text-gray-900" style={{ fontFamily: "'Libre Baskerville', serif" }}>{t.title}</h3>
                  <p className="text-gray-500 text-sm mt-1">{t.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Stats */}
      <section className="py-16 bg-blue-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { icon: Users, value: '25+', label: 'Team Members' },
              { icon: Medal, value: '7+', label: 'Years Experience' },
              { icon: Handshake, value: '50+', label: 'Active Partners' },
              { icon: TrendUp, value: '15+', label: 'Countries Served' },
            ].map(s => (
              <div key={s.label}>
                <s.icon size={28} className="text-blue-300 mx-auto mb-2" />
                <p className="text-2xl lg:text-3xl font-bold text-white" style={{ fontFamily: "'Libre Baskerville', serif" }}>{s.value}</p>
                <p className="text-blue-200 text-sm mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
